import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useState } from 'react';

import { Badge } from '../components/ui/Badge.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Card } from '../components/ui/Card.jsx';
import { EmptyState } from '../components/ui/EmptyState.jsx';
import { Skeleton } from '../components/ui/Skeleton.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { auditApi } from '../services/auditApi.js';
import { formatDateTime } from '../utils/formatters.js';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const ENTITY_ID_SHORT_LENGTH = 8;

const MODULES = ['auth', 'admin', 'finance', 'hr', 'inventory', 'sales', 'projects', 'production'];
const ACTIONS = ['create', 'update', 'delete', 'login', 'login_failed', 'logout', 'register'];

// auth/admin have audit-specific labels; business modules reuse the nav labels.
const MODULE_LABEL_KEYS = {
  auth: 'audit.module.auth',
  admin: 'audit.module.admin',
  finance: 'nav.section.finance',
  hr: 'nav.section.hr',
  inventory: 'nav.section.inventory',
  sales: 'nav.section.sales',
  projects: 'nav.section.projects',
  production: 'nav.section.production',
};

const ACTION_BADGE_VARIANTS = {
  create: 'success',
  update: 'info',
  delete: 'danger',
  login: 'neutral',
  login_failed: 'danger',
  logout: 'neutral',
  register: 'info',
};

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function entityLabel(row) {
  if (!row.entity) {
    return '-';
  }
  const shortId = row.entityId ? ` #${String(row.entityId).slice(0, ENTITY_ID_SHORT_LENGTH)}` : '';
  return `${row.entity}${shortId}`;
}

function AuditDetailRow({ row, colSpan, id }) {
  return (
    <tr className="audit-detail-row" id={id}>
      <td colSpan={colSpan}>
        <div className="audit-detail">
          <div className="mono text-sm">
            {row.method} {row.path}
          </div>
          {row.requestId && <div className="mono muted text-sm">{row.requestId}</div>}
          {row.payload && <pre className="mono">{JSON.stringify(row.payload, null, 2)}</pre>}
        </div>
      </td>
    </tr>
  );
}

export function AuditLogCard() {
  const { t } = useTranslation();
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);

  const q = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);

  // The debounced search term changes outside any event handler, so the page
  // reset uses the render-time state-adjustment pattern instead of an effect.
  const [prevQ, setPrevQ] = useState(q);
  if (q !== prevQ) {
    setPrevQ(q);
    setPage(0);
  }

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: ['audit', { module: moduleFilter, action: actionFilter, q, from, to, page }],
    queryFn: () =>
      auditApi.list({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        module: moduleFilter || undefined,
        action: actionFilter || undefined,
        q: q || undefined,
        from: from || undefined,
        to: to || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.items || [];
  const total = data?.meta?.total ?? 0;
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * PAGE_SIZE + rows.length);
  const hasNext = page * PAGE_SIZE + rows.length < total;

  const columns = [
    t('audit.col.time'),
    t('audit.col.user'),
    t('audit.col.module'),
    t('audit.col.action'),
    t('audit.col.entity'),
    t('audit.col.status'),
  ];

  function toggleRow(row) {
    setExpandedId((current) => (current === row.id ? null : row.id));
  }

  return (
    <Card>
      <div className="toolbar audit-toolbar">
        <select
          className="select"
          value={moduleFilter}
          onChange={(e) => {
            setModuleFilter(e.target.value);
            setPage(0);
          }}
          aria-label={t('audit.col.module')}
        >
          <option value="">{t('audit.filter.allModules')}</option>
          {MODULES.map((m) => (
            <option key={m} value={m}>
              {t(MODULE_LABEL_KEYS[m])}
            </option>
          ))}
        </select>
        <select
          className="select"
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setPage(0);
          }}
          aria-label={t('audit.col.action')}
        >
          <option value="">{t('audit.filter.allActions')}</option>
          {ACTIONS.map((a) => (
            <option key={a} value={a}>
              {t(`audit.action.${a}`)}
            </option>
          ))}
        </select>
        <input
          className="input"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search')}
          aria-label={t('common.search')}
        />
        <input
          className="input audit-date"
          type="date"
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            setPage(0);
          }}
          aria-label={t('audit.filter.from')}
        />
        <input
          className="input audit-date"
          type="date"
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setPage(0);
          }}
          aria-label={t('audit.filter.to')}
        />
      </div>

      {error && <div className="alert danger">{error.message || t('common.loadError')}</div>}

      {isLoading ? (
        <div className="table-wrap">
          <Skeleton variant="table" />
        </div>
      ) : rows.length === 0 ? (
        <div className="table-wrap">
          <EmptyState message={t('audit.empty')} />
        </div>
      ) : (
        <div className={isPlaceholderData ? 'table-wrap audit-stale' : 'table-wrap'}>
          <table className="table">
            <thead>
              <tr>
                {columns.map((label) => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const hasDetail = Boolean(row.payload || row.path);
                const isExpanded = expandedId === row.id;
                const detailRowId = `audit-detail-${row.id}`;
                return (
                  <Fragment key={row.id}>
                    <tr
                      className={hasDetail ? 'audit-row expandable' : 'audit-row'}
                      onClick={hasDetail ? () => toggleRow(row) : undefined}
                    >
                      <td>{formatDateTime(row.occurredAt)}</td>
                      <td>{row.userEmail || '-'}</td>
                      <td>
                        <Badge variant="info">{t(MODULE_LABEL_KEYS[row.module]) || row.module}</Badge>
                      </td>
                      <td>
                        <Badge variant={ACTION_BADGE_VARIANTS[row.action] || 'neutral'}>
                          {ACTIONS.includes(row.action) ? t(`audit.action.${row.action}`) : row.action}
                        </Badge>
                      </td>
                      <td className="mono">{entityLabel(row)}</td>
                      <td className={row.statusCode >= 400 ? 'audit-status-error' : undefined}>
                        {row.statusCode}
                        {hasDetail && (
                          <button
                            type="button"
                            className="audit-toggle"
                            aria-expanded={isExpanded}
                            aria-controls={detailRowId}
                            aria-label={t('audit.toggleDetails')}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(row);
                            }}
                          >
                            {isExpanded ? '▾' : '▸'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && hasDetail && (
                      <AuditDetailRow row={row} colSpan={columns.length} id={detailRowId} />
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 && (
        <div className="audit-pagination">
          <Button size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            ‹ {t('audit.prev')}
          </Button>
          <span className="muted text-sm">
            {rangeStart}-{rangeEnd} {t('audit.pagination.of')} {total}
          </span>
          <Button size="sm" disabled={!hasNext || isPlaceholderData} onClick={() => setPage((p) => p + 1)}>
            {t('audit.next')} ›
          </Button>
        </div>
      )}
    </Card>
  );
}
